import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '@/context/StoreContext';
import { useFunnelState } from '@/features/funnel';
import { frontDoorHref, hasLiveSession, PAGE_PATHS, useRouter } from '@/lib/router';
import { AuthPill } from './AuthPill';
import { NAV_ITEMS } from './navItems';
import { SigilMark } from './SigilMark';
import { summonEntries, summonKeyAction, summonRows, type SummonEntry } from './summon';
import { SummonOverlay } from './SummonOverlay';

const SCROLLED_AT_PX = 50;

interface SummonState {
  /** The dialog is created on the first open and kept afterwards (its close fades out). */
  readonly mounted: boolean;
  readonly open: boolean;
  readonly session: number;
  readonly entries: readonly SummonEntry[];
}

/**
 * The fixed header (bundle `us()` + omega-summon.js mount + gauntlet pill, ui-layer.md §5.2).
 * UI-R1: the hidden `ul.nav__links`, `.nav__mobile-menu` and `.nav__backdrop` are not rendered;
 * the sigil (≥ 900 px) and the hamburger (< 900 px) both summon the navigation dialog.
 */
export function Nav() {
  const { navigate, pathname } = useRouter();
  const { cart, setCartOpen } = useStore();
  const { revealedRungs } = useFunnelState();
  const [scrolled, setScrolled] = useState(false);
  const [summon, setSummon] = useState<SummonState>({ mounted: false, open: false, session: 0, entries: [] });
  const openRef = useRef(false);
  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > SCROLLED_AT_PX);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const show = useCallback(
    (opener: Element | null) => {
      const signedIn = hasLiveSession();
      const rows = summonRows(
        revealedRungs,
        NAV_ITEMS,
        (href) => frontDoorHref(href, signedIn, window.location.href).href,
      );
      if (rows.length === 0) return;
      returnFocus.current = opener instanceof HTMLElement ? opener : null;
      openRef.current = true;
      setSummon((previous) => ({
        mounted: true,
        open: true,
        session: previous.session + 1,
        entries: summonEntries(rows),
      }));
    },
    [revealedRungs],
  );

  const close = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;
    setSummon((previous) => ({ ...previous, open: false }));
  }, []);

  const toggle = useCallback(
    (opener: Element | null) => {
      if (openRef.current) close();
      else show(opener);
    },
    [close, show],
  );

  // Ctrl/⌘+K from page load (UI-R3).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (summonKeyAction(event, openRef.current) !== 'toggle') return;
      event.preventDefault();
      toggle(document.activeElement);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [toggle]);

  // Focus returns to the control that opened the dialog once it has closed (after the
  // dialog's focus containment has been released).
  useEffect(() => {
    if (summon.open || !summon.mounted) return;
    const target = returnFocus.current;
    returnFocus.current = null;
    if (target?.isConnected) target.focus();
  }, [summon.open, summon.mounted]);

  // A route change (e.g. browser Back) dismisses the dialog.
  useEffect(() => {
    close();
  }, [pathname, close]);

  const count = cart?.totalQuantity ?? 0;

  return (
    <>
      <nav className={'nav ' + (scrolled ? 'nav--scrolled' : '')}>
        <img
          src="./assets/logos/logo-full-red.png"
          alt="MUTATIO618"
          className="nav__logo"
          onClick={() => {
            navigate(PAGE_PATHS.home);
          }}
        />
        <div className="nav__actions">
          <button
            type="button"
            className="omega-sigil"
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={summon.open}
            onClick={(event) => {
              event.preventDefault();
              show(event.currentTarget);
            }}
          >
            <SigilMark />
          </button>
          <AuthPill />
          <button
            className="nav__cart-btn"
            onClick={() => {
              setCartOpen(true);
            }}
            aria-label="Open cart"
            data-magnetic="true"
          >
            🛒{count > 0 && <span className="nav__cart-badge">{count}</span>}
          </button>
          <button
            className="nav__hamburger "
            onClick={(event) => {
              toggle(event.currentTarget);
            }}
            aria-label="Toggle menu"
            aria-haspopup="dialog"
            aria-expanded={summon.open}
            data-omega-bound="1"
          >
            <span className="nav__hamburger-line"></span>
            <span className="nav__hamburger-line"></span>
            <span className="nav__hamburger-line"></span>
            <span className="omega-mark">
              <SigilMark />
            </span>
          </button>
        </div>
      </nav>
      {summon.mounted &&
        createPortal(
          <SummonOverlay
            open={summon.open}
            session={summon.session}
            entries={summon.entries}
            pathname={pathname}
            onClose={close}
          />,
          document.body,
        )}
    </>
  );
}
